import {
  useId,
  useState,
  useEffect,
  useRef,
  type JSX,
  type ReactNode,
} from 'react';
import { type PopperPlacementType } from '@mui/material/Popper';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { TooltipRich } from '../tooltip/rich';
import { type TooltipProps, Tooltip } from '../tooltip/tooltip';
import { useInfoIconStyles } from './infoIcon.styles';
import { type AriaProps } from '../../../types';
import { useIntl } from '@doxyme/locale';
import { useTheme } from '../../../theme';

type InfoIconEventDetail = {
  target: HTMLElement | null;
};

export type InfoIconPopoverPlacement =
  | PopperPlacementType
  | TooltipProps['placement'];

export type InfoIconProps = AriaProps & {
  title?: string | ReactNode;
  action?: string | JSX.Element;
  popoverPlacement?: InfoIconPopoverPlacement;
  popoverFallbackPlacements?: TooltipProps['placement'][];
  onShow?: () => void;
};

export const InfoIcon = ({
  title,
  action,
  popoverPlacement = 'top',
  popoverFallbackPlacements,
  onShow,
  ...rest
}: InfoIconProps): JSX.Element => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const styles = useInfoIconStyles();
  const id = useId();

  const handleToggleTooltip = (newState: boolean) => {
    if (newState) {
      const infoIconOpenEvent = new CustomEvent<InfoIconEventDetail>(
        'onInfoIconOpen',
        { detail: { target: buttonRef.current } },
      );
      window.dispatchEvent(infoIconOpenEvent);
    }

    setIsTooltipOpen(newState);
  };

  const handleInfoIconOpened = (
    event: CustomEventInit<InfoIconEventDetail>,
  ): void => {
    if (event.detail?.target !== buttonRef.current) {
      setIsTooltipOpen(false);
    }
  };

  useEffect(() => {
    window.addEventListener('onInfoIconOpen', handleInfoIconOpened);

    return (): void => {
      window.removeEventListener('onInfoIconOpen', handleInfoIconOpened);
    };
  }, []);

  const onClickAway = (event: UIEvent): void => {
    const target = event.target as Node;
    const clickedOnOpener = buttonRef.current?.contains(target) ?? false;

    if (isTooltipOpen && !clickedOnOpener) {
      setIsTooltipOpen(false);
    }
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Tooltip
        id={id}
        title={
          typeof title === 'string' ? (
            <TooltipRich body={title} actionText={action} />
          ) : (
            title
          )
        }
        placement={popoverPlacement as TooltipProps['placement']}
        onOpen={() => {
          onShow?.();
          const infoIconOpenEvent = new CustomEvent<InfoIconEventDetail>(
            'onInfoIconOpen',
            { detail: { target: buttonRef.current } },
          );
          window.dispatchEvent(infoIconOpenEvent);
        }}
        popoverFallbackPlacements={popoverFallbackPlacements}
        open={isTooltipOpen}
        onClose={(e) => {
          // PROD-1751 - When the button is clicked, it receives focus. If a blur event occurs from clicking inside, it triggers the tooltip to close.
          if (e.target !== buttonRef.current && e.type !== 'blur') {
            handleToggleTooltip(false);
          }
        }}
      >
        <Box css={styles.container}>
          <button
            type='button'
            css={styles.button}
            aria-label={formatMessage({
              id: 'infoIcon.help',
            })}
            onTouchStart={() => handleToggleTooltip(true)}
            onMouseEnter={() => handleToggleTooltip(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleToggleTooltip(true);
              }
              if (event.key === 'Escape') {
                handleToggleTooltip(false);
              }
            }}
            ref={buttonRef}
            {...rest}
          >
            <Box css={styles.iconWrapper}>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='6'
                height='8'
                viewBox='0 0 6 8'
                fill='none'
              >
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M2.92042 1.6C2.61856 1.6 2.35452 1.76688 2.2176 2.01721C2.00558 2.40485 1.51946 2.54721 1.13183 2.33519C0.744198 2.12317 0.601835 1.63706 0.813855 1.24942C1.22012 0.506648 2.01053 0 2.92042 0C4.13222 0 4.97393 0.80514 5.21357 1.7512C5.45449 2.70228 5.08848 3.8104 3.99373 4.35777C3.82623 4.44152 3.72042 4.61272 3.72042 4.8C3.72042 5.24183 3.36225 5.6 2.92042 5.6C2.47859 5.6 2.12042 5.24183 2.12042 4.8C2.12042 4.00669 2.56863 3.28147 3.27819 2.92669C3.64199 2.74479 3.73543 2.43179 3.66256 2.14408C3.5884 1.85134 3.34308 1.6 2.92042 1.6Z'
                  fill={theme.infoIcon.icon}
                />
                <path
                  d='M3.72042 7.2C3.72042 7.64183 3.36225 8 2.92042 8C2.47859 8 2.12042 7.64183 2.12042 7.2C2.12042 6.75817 2.47859 6.4 2.92042 6.4C3.36225 6.4 3.72042 6.75817 3.72042 7.2Z'
                  fill={theme.infoIcon.icon}
                />
              </svg>
            </Box>
          </button>
        </Box>
      </Tooltip>
    </ClickAwayListener>
  );
};
